import React from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getEventById, rsvpToEvent, deleteEvent } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { 
  ArrowLeft,
  Calendar, 
  Clock,
  MapPin,
  Users,
  User,
  Edit3,
  Trash2,
  Eye,
  Share2,
  UserPlus,
  Video,
  Globe,
  AlertTriangle,
  Music,
  GraduationCap,
  Briefcase,
  Coffee,
  ExternalLink
} from 'lucide-react';
import { 
  IndividualViewPageLayout, 
  SidebarSection, 
  ActionButtons, 
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';

export default function ViewEventPage() {
  const { eventId, slug } = useParams<{ eventId: string; slug?: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const eventsListPath = locationSectionPath(locSlug, 'events');

  // Fetch event data
  const { 
    data: event, 
    isLoading, 
    error 
  } = useQuery(getEventById, { eventId: eventId! });

  const rsvpAction = useAction(rsvpToEvent);
  const deleteEventAction = useAction(deleteEvent);

  const getEventTypeIcon = (eventType: string | null) => {
    switch (eventType) {
      case 'workshop': return <GraduationCap className="w-5 h-5" />;
      case 'meeting': return <Briefcase className="w-5 h-5" />;
      case 'cultural': return <Music className="w-5 h-5" />;
      case 'business': return <Briefcase className="w-5 h-5" />;
      case 'social': return <Coffee className="w-5 h-5" />;
      case 'educational': return <GraduationCap className="w-5 h-5" />;
      default: return <Calendar className="w-5 h-5" />;
    }
  };

  const formatEventDateTime = (startTime: string, endTime: string, timezone: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const sameDay = start.toDateString() === end.toDateString();
    
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    if (sameDay) {
      return {
        date: start.toLocaleDateString('en-US', dateOptions),
        time: `${start.toLocaleTimeString('en-US', timeOptions)} - ${end.toLocaleTimeString('en-US', timeOptions)}`,
        timezone: timezone
      };
    } else {
      return {
        date: `${start.toLocaleDateString('en-US', dateOptions)} - ${end.toLocaleDateString('en-US', dateOptions)}`,
        time: `${start.toLocaleTimeString('en-US', timeOptions)} - ${end.toLocaleTimeString('en-US', timeOptions)}`,
        timezone: timezone
      };
    }
  };

  const handleRSVP = async () => {
    if (!eventId) return;
    
    try {
      await rsvpAction({ eventId });
      // Refresh the page to show updated attendance
      window.location.reload();
    } catch (error: any) {
      alert(error.message || 'Failed to RSVP to event');
    }
  };

  const isUserRegistered = () => {
    if (!user || !event?.attendees) return false;
    return event.attendees.some((attendee: any) => attendee.user.id === user.id);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading event...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Event Not Found</h3>
            <p className="text-gray-600 mb-4">The event you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate(eventsListPath)}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors"
            >
              Back to Events
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = !!user && (user.id === event.organizerId || isAdministrator(user));
  const dateTime = formatEventDateTime(event.startTime, event.endTime, event.timezone);
  const isRegistered = isUserRegistered();
  const eventDate = new Date(event.startTime);
  const isEventPast = eventDate < new Date();
  const canRegister = !isRegistered && !isEventPast && event.requiresRegistration;

  const currentPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const breadcrumbItems = [
    {
      label: 'About',
      dropdownItems: [
        { label: 'Notices', href: locationSectionPath(locSlug, 'notices') },
        { label: 'Events/Activities', href: eventsListPath },
        { label: 'Tenants', href: locationSectionPath(locSlug, 'tenants') },
        { label: 'Facilities', href: locationSectionPath(locSlug, 'facilities') },
      ]
    },
    {
      label: 'Events/Activities',
      isClickable: true,
      href: eventsListPath
    }
  ];

  const sidebarContent = (
    <>
      {isOwner && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={locationSectionPath(locSlug, 'events', eventId!, 'edit')}
              icon={Edit3}
            >
              Edit Event
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={async () => {
                if (confirm('Are you sure you want to delete this event?')) {
                  try {
                    await deleteEventAction({ eventId: eventId! });
                    navigate(eventsListPath);
                  } catch (error: any) {
                    alert(error.message || 'Failed to delete event');
                  }
                }
              }}
              icon={Trash2}
            >
              Delete Event
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Event Type" value={event.eventType || 'Event'} />
          <DetailField label="Status" value={event.status || 'N/A'} />
          <DetailField label="Visibility" value={event.visibility || 'N/A'} />
          <DetailField label="Created" value={new Date(event.createdAt).toLocaleDateString()} />
        </div>
      </SidebarSection>

      {event.requiresRegistration && (
        <SidebarSection title="Registration">
          {isRegistered ? (
            <div className="text-center">
              <div className="text-green-600 mb-2">
                <UserPlus className="w-8 h-8 mx-auto" />
              </div>
              <p className="font-medium text-gray-900 mb-2">You're registered!</p>
              <p className="text-sm text-gray-600">
                We'll send you a reminder before the event.
              </p>
            </div>
          ) : isEventPast ? (
            <div className="text-center">
              <p className="text-gray-600">This event has ended.</p>
            </div>
          ) : canRegister ? (
            <div className="text-center">
              {user ? (
                <>
                  <button
                    onClick={handleRSVP}
                    className="w-full bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors mb-3"
                  >
                    <UserPlus className="w-4 h-4 inline mr-2" />
                    Register for Event
                  </button>
                  {event.maxAttendees && event.attendees && event.attendees.length >= event.maxAttendees && (
                    <p className="text-sm text-red-600">Event is full</p>
                  )}
                </>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex w-full items-center justify-center bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors mb-3"
                >
                  <UserPlus className="w-4 h-4 inline mr-2" />
                  Log in to Register
                </Link>
              )}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-600">Registration not required</p>
            </div>
          )}
        </SidebarSection>
      )}
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
                {/* Event Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    {getEventTypeIcon(event.eventType)}
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
                      <p className="text-gray-600">
                        Organized by {event.organizer.username || event.organizer.email}
                      </p>
                    </div>
                  </div>
                </div>
              {/* Cover Image */}
              {event.coverImage && (
                <div className="mb-6">
                  <img 
                    src={event.coverImage} 
                    alt={event.title}
                    className="w-full h-64 object-cover border border-gray-200"
                  />
                </div>
              )}

              {/* Event Status */}
              {isEventPast && (
                <div className="bg-gray-50 border border-gray-200 p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">This event has ended</span>
                  </div>
                </div>
              )}

              {/* Event Details */}
              <div className="bg-white border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Details</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">{dateTime.date}</div>
                      <div className="text-gray-600">{dateTime.time}</div>
                      <div className="text-sm text-gray-500">{dateTime.timezone}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {event.isVirtual ? 'Virtual Event' : (event.venueName || 'TBD')}
                      </div>
                      {event.isVirtual && event.meetingUrl && (
                        <a 
                          href={event.meetingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-1"
                        >
                          <Video className="w-4 h-4" />
                          Join Meeting
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {!event.isVirtual && event.venueAddress && (
                        <div className="text-gray-600 mt-1">{event.venueAddress}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-500" />
                    <div>
                      <span className="font-medium text-gray-900">
                        {event.attendees?.length || 0} attending
                      </span>
                      {event.maxAttendees && (
                        <span className="text-gray-600"> (max {event.maxAttendees})</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <span className="font-medium text-gray-900">
                        Organized by {event.organizer.username || event.organizer.email}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About This Event</h2>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
                </div>
              </div>

              {/* Attendees */}
              {event.attendees && event.attendees.length > 0 && (
                <div className="bg-white border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Attendees ({event.attendees.length})
                  </h2>
                  <div className="space-y-3">
                    {event.attendees.map((attendee: any) => (
                      <div key={attendee.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {attendee.user.username || attendee.user.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            Registered {new Date(attendee.registeredAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
    </IndividualViewPageLayout>
  );
}


